import { inject, Injectable } from '@angular/core';
import { Dictionary } from '@ngrx/entity';
import { Store } from '@ngrx/store';
import { switchMap } from 'rxjs';

import { runtimeSelectors } from '@blaze-runtime/engine-data-access';
import { CanvasElementId, PropsOf } from '@blaze-runtime/engine-domain';
import { fromRouter } from '@blaze-shared/app-state-data-access';
import {
  FormContextCalendar,
  FormListErrors,
  FormValidationErrors,
} from '@blaze-shared/elements-domain';
import { syncSelect } from '@blaze-shared/platform-data-access';
import { SchemaService } from '@blaze-shared/schema-data-access';
import { BaseSchema, DatasetColumn, DatasetRow } from '@blaze-shared/schema-domain';

import {
  getElementById,
  getElementsBoundToForm,
} from '../../../+state/elements/elements.selectors';
import { isEditableForm } from '../../../canvas-elements/element-model/element-type-guards';

import { validateColumn } from './column-validation';
import { areFormListRowsBlank, isFormValueBlank } from './form-blankness';
import { validateRowLimits } from './form-list-row-limits';
import { FormStateService } from './form-state.service';
import { buildFormValidationModel, FormValidationModel } from './form-validation-model';
import {
  collectPropEditorValidationRules,
  PropEditorFieldValidations,
} from './prop-editor-validation-rules';

/** Everything a form's columns are validated against, read once per validation pass. */
interface FormValidationContext {
  formContext: FormContextCalendar | undefined;
  formModel: FormValidationModel | undefined;
  propEditorValidations: PropEditorFieldValidations;
}

/**
 * Validates what the user entered into a formlike container and publishes the outcome as
 * the container's form state. The rules themselves are pure functions in this folder; this
 * service is what reads the form's context out of the store and reports the verdict back.
 */
@Injectable({ providedIn: 'root' })
export class FormValidationService {
  private readonly formState = inject(FormStateService);
  private readonly schemaService = inject(SchemaService);
  private readonly store = inject(Store);

  /** The accounts table's rows are written by the backend, never demanded from the user. */
  private accountsTableName: string | undefined;

  constructor() {
    this.store
      .select(fromRouter.getProjectId)
      .pipe(switchMap((projectId) => this.schemaService.obtainAccountsTableName(projectId)))
      .subscribe((accountsTableName) => {
        this.accountsTableName = accountsTableName;
      });
  }

  /**
   * Validates a form's value.
   * @returns the id of every column that failed, keyed by column name, so the form can
   * point the user at the offending fields
   */
  validateForm(
    formElementId: CanvasElementId,
    formSchema: BaseSchema | undefined,
    value: Record<string, any>,
    blankValue: Record<string, any>,
  ): Record<string, string> {
    const errors: Dictionary<FormValidationErrors> = {};
    const failedColumnIds: Record<string, string> = {};
    const isBlank = isFormValueBlank(value, blankValue);
    if (!formSchema) {
      this.formState.updateValidity(formElementId, errors, isBlank);
      return failedColumnIds;
    }

    const context = this.readValidationContext(formElementId);
    for (const column of formSchema.columns) {
      const columnName = this.schemaService.getColumnNameLatest(column);
      const error = this.validateColumnValue(column, columnName, value, context);
      if (error !== undefined) {
        errors[columnName] = error;
        failedColumnIds[columnName] = column.id ?? column.name;
      }
    }

    this.formState.updateValidity(formElementId, errors, isBlank);
    return failedColumnIds;
  }

  /** Validates a single row of a form list, plus the list's row-count limits. */
  validateFormListRow(
    formElementId: CanvasElementId,
    formSchema: BaseSchema | undefined,
    dataRow: Record<string, any>,
    rowIdColumn: string,
    displayIndex: number,
    value: DatasetRow[],
    props: PropsOf<'editableList'> | undefined,
  ): void {
    if (!dataRow || !formSchema) {
      return;
    }

    const isBlank = areFormListRowsBlank(value);
    const context = this.readValidationContext(formElementId);
    const errors: Dictionary<FormValidationErrors> = {};

    if (!dataRow._isDeleted) {
      for (const column of getFormListColumns(formSchema)) {
        const columnName = this.schemaService.getColumnNameLatest(column);
        const error = this.validateColumnValue(column, columnName, dataRow, context);
        if (error !== undefined) {
          errors[columnName] = error;
        }
      }
    }

    this.formState.updateRowValidity({
      formElementId,
      rowId: dataRow[rowIdColumn] as string,
      rowState: {
        errors,
        valid: !Object.keys(errors).length,
        displayIndex,
      },
      formBlank: isBlank,
    });
    this.formState.updateRowValidity({
      formElementId,
      rowId: formElementId,
      rowState: validateRowLimits(value, props),
      formBlank: isBlank,
    });
  }

  /** Validates every row of a form list, plus the list's row-count limits. */
  validateFormListRows(
    formElementId: CanvasElementId,
    formSchema: BaseSchema | undefined,
    value: Record<string, any>[],
    rowIdColumn: string,
    props: PropsOf<'editableList'> | undefined,
  ): void {
    if (!value) {
      return;
    }

    const errors: Dictionary<FormListErrors> = {};
    const isBlank = areFormListRowsBlank(value);
    if (!formSchema) {
      this.formState.updateValidity(formElementId, errors, isBlank);
      return;
    }

    const context = this.readValidationContext(formElementId);
    const formListColumns = getFormListColumns(formSchema);
    for (let index = 0, displayIndex = 0; index < value.length; index++) {
      const row = value[index];
      if (row._isDeleted) {
        continue;
      }

      const rowId = row[rowIdColumn];
      for (const column of formListColumns) {
        const columnName = this.schemaService.getColumnNameLatest(column);
        const error = this.validateColumnValue(column, columnName, row, context);
        if (error !== undefined) {
          if (!errors[rowId]) {
            errors[rowId] = { valid: true, errors: {}, displayIndex };
          }
          errors[rowId].errors[columnName] = error;
          errors[rowId].valid = false;
        }
      }
      displayIndex++;
    }

    errors[formElementId] = validateRowLimits(value, props);
    this.formState.updateValidity(formElementId, errors, isBlank);
  }

  private validateColumnValue(
    column: DatasetColumn,
    columnName: string,
    value: Record<string, any>,
    { formContext, formModel, propEditorValidations }: FormValidationContext,
  ): Record<string, boolean | string | number> | undefined {
    return validateColumn(column, columnName, value, {
      modelField: formModel?.[columnName],
      formContext,
      accountsTableName: this.accountsTableName,
      propEditorValidation: propEditorValidations[columnName],
    });
  }

  private readValidationContext(formElementId: CanvasElementId): FormValidationContext {
    const formElement = syncSelect(this.store, getElementById(formElementId));
    // why: only an editable form's fields pick up required/invalid state at runtime, so
    // there is nothing to read for the rest
    const elementMeta = isEditableForm(formElement)
      ? syncSelect(this.store, runtimeSelectors.selectElementMeta)
      : undefined;

    return {
      formContext: syncSelect(this.store, runtimeSelectors.selectFormContextById(formElementId)),
      formModel: buildFormValidationModel(formElement, elementMeta, (column) =>
        this.schemaService.getColumnNameLatest(column),
      ),
      propEditorValidations: collectPropEditorValidationRules(
        syncSelect(this.store, getElementsBoundToForm(formElementId)),
        (fieldKey) => this.schemaService.getColumnName(fieldKey),
      ),
    };
  }
}

/**
 * A form list bound to a multi-value column validates that column's inner columns; every
 * other list validates the schema's columns directly.
 */
function getFormListColumns(formSchema: BaseSchema): DatasetColumn[] {
  return formSchema.columns.length === 1 && formSchema.columns[0].multipleValues
    ? (formSchema.columns[0].dataTypeDef?.columns ?? [])
    : formSchema.columns;
}
